import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, Box, Button, Alert, Card, CardContent,
  Tabs, Tab, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import supabase from '../../supabaseClient';
import WorkerCameraScanner from '../Worker/WorkerCameraScanner';
import NoticeboardForWorker from '../Manager/Home/Calendar/Noticeboard/NoticeboardForWorker';

// ── Fullscreen success/error overlay ─────────────────────────────────────────
function ScanOverlay({ state, msg }) {
  if (!state) return null;
  const ok = state === 'success';
  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1200,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: ok
        ? 'radial-gradient(ellipse at center, #d0f0e0 0%, #b8e8cc 100%)'
        : 'radial-gradient(ellipse at center, #f5d0d0 0%, #e8b8b8 100%)',
      animation: 'fadeIn 0.25s ease',
      '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
    }}>
      <Box sx={{
        width: 140, height: 140, borderRadius: '50%',
        bgcolor: ok ? 'rgba(76,175,80,0.15)' : 'rgba(198,40,40,0.12)',
        border: `3px solid ${ok ? '#4caf50' : '#c62828'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4,
      }}>
        {ok
          ? <CheckIcon sx={{ fontSize: 72, color: '#4caf50' }} />
          : <CloseIcon sx={{ fontSize: 72, color: '#c62828' }} />}
      </Box>
      <Typography variant="h3" sx={{ fontWeight: 800, color: ok ? '#1b5e20' : '#7f0000', mb: 1 }}>
        {ok ? 'Operacja udana!' : 'Błąd odczytu!'}
      </Typography>
      {msg && (
        <Typography variant="body1" sx={{ color: ok ? '#388e3c' : '#c62828', mt: 1, px: 4, textAlign: 'center', fontWeight: 500 }}>
          {msg}
        </Typography>
      )}
    </Box>
  );
}

// ── Helper formatujący minuty na tekst Xh Ymin ──────────────────────────────
const formatMinutesToHours = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  return `${h}h ${m}min`;
};

// ── Main component ────────────────────────────────────────────────────────────
function DriverAttendanceGateKeeper() {
  const [activeTab, setActiveTab] = useState(0); // 0 = Start/Stop, 1 = Statystyki
  const [idConfig, setIdConfig] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRecord, setActiveRecord] = useState(null);
  const [hoursToday, setHoursToday] = useState(0);
  const [clock, setClock] = useState('');
  
  const [overlay, setOverlay] = useState(null);
  const [overlayMsg, setOverlayMsg] = useState('');

  // ── Stany dla statystyk miesięcznych ──
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [monthlyLogs, setMonthlyLogs] = useState([]);
  const [monthlyTotalMinutes, setMonthlyTotalMinutes] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false);

  const cameraRef = useRef();

  // Clock
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch Logged User & Employee Data
  useEffect(() => {
    const initUser = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setLoading(false);
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('id, id_configuration')
          .eq('id', session.user.id)
          .single();

        if (profile) {
          setIdConfig(profile.id_configuration);

          const { data: empData, error: empErr } = await supabase
            .from('employees')
            .select('id, name, surname, id_configuration')
            .eq('id_profiles', profile.id)
            .maybeSingle();

          if (empErr || !empData) {
            console.error('Nie znaleziono pracownika dla tego konta:', empErr);
          } else {
            setEmployee(empData);
            await fetchEmployeeStatus(empData.id);
          }
        }
      } catch (e) {
        console.error('Błąd inicjalizacji użytkownika:', e);
      } finally {
        setLoading(false);
      }
    };

    initUser();
  }, []);

  // Ładowanie statystyk miesięcznych przy zmianie miesiąca lub przełączeniu zakładki
  useEffect(() => {
    if (employee && activeTab === 1) {
      fetchMonthlyStats(employee.id, selectedMonth);
    }
  }, [employee, activeTab, selectedMonth]);

  const showOverlay = (type, msg = '') => {
    setOverlay(type); setOverlayMsg(msg);
    setTimeout(() => { setOverlay(null); setOverlayMsg(''); }, 3000);
  };

  const playSuccessSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start(); osc.stop(ctx.currentTime + 0.2);
    } catch (e) {}
  };

  // Pobieranie dzisiejszego stanu pracy
  const fetchEmployeeStatus = async (empId) => {
    const today = new Date().toISOString().split('T')[0];
    const { data: todayRecords } = await supabase.from('attendance')
      .select('id, check_in, check_out')
      .eq('employee_id', empId)
      .eq('type', 'work')
      .gte('check_in', `${today}T00:00:00Z`)
      .lte('check_in', `${today}T23:59:59Z`)
      .order('check_in', { ascending: false });

    const active = todayRecords?.find(r => !r.check_out) || null;
    setActiveRecord(active);

    const now = Date.now();
    const minutes = (todayRecords || []).reduce((total, rec) => {
      const start = new Date(rec.check_in).getTime();
      const end = rec.check_out ? new Date(rec.check_out).getTime() : now;
      return total + (end - start) / 1000 / 60;
    }, 0);

    setHoursToday(minutes);
  };

  // Pobieranie wpisów z danego miesiąca
  const fetchMonthlyStats = async (empId, date) => {
    setLoadingStats(true);
    try {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1).toISOString();
      const lastDay = new Date(year, month + 1, 0, 23, 59, 59).toISOString();

      const { data: records } = await supabase.from('attendance')
        .select('id, check_in, check_out')
        .eq('employee_id', empId)
        .eq('type', 'work')
        .gte('check_in', firstDay)
        .lte('check_in', lastDay)
        .order('check_in', { ascending: false });

      const logs = records || [];
      const now = Date.now();
      
      const totalMinutes = logs.reduce((acc, rec) => {
        const start = new Date(rec.check_in).getTime();
        const end = rec.check_out ? new Date(rec.check_out).getTime() : now;
        return acc + (end - start) / 1000 / 60;
      }, 0);

      setMonthlyLogs(logs);
      setMonthlyTotalMinutes(totalMinutes);
    } catch (e) {
      console.error('Błąd pobierania statystyk:', e);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleStartStop = async () => {
    if (!employee) return;

    try {
      const { data: lastRec } = await supabase.from('attendance')
        .select('check_in, check_out').eq('employee_id', employee.id).eq('type', 'work')
        .order('check_in', { ascending: false }).limit(1).maybeSingle();

      if (lastRec) {
        const diff = (Date.now() - new Date(lastRec.check_out || lastRec.check_in).getTime()) / 1000;
        if (diff < 60) throw new Error(`${employee.name}! Odczekaj jeszcze ${Math.ceil(60 - diff)} sek.`);
      }

      const action = activeRecord ? 'WYJSCIE' : 'WEJSCIE';
      const now = new Date();

      if (activeRecord) {
        const { data: deviceLogs } = await supabase.from('device_attendance')
          .select('type, device_id').eq('attendance_id', activeRecord.id)
          .order('timestamp', { ascending: true });

        if (deviceLogs?.length > 0) {
          const deviceStatusMap = {};
          deviceLogs.forEach(log => { deviceStatusMap[log.device_id] = log.type; });
          const hasOpenMachine = Object.values(deviceStatusMap).includes('START_WORK');
          if (hasOpenMachine) {
            const openId = Object.keys(deviceStatusMap).find(k => deviceStatusMap[k] === 'START_WORK');
            let machineName = 'jednej z maszyn';
            if (openId) {
              const { data: dev } = await supabase.from('devices').select('name').eq('id', openId).maybeSingle();
              if (dev) machineName = dev.name;
            }
            throw new Error(`Blokada wyjścia! Nie zakończyłeś pracy na: "${machineName}".`);
          }
        }
      }

      let photoPath = null;
      if (cameraRef.current) {
        const blob = await cameraRef.current.capture();
        if (blob) {
          const path = `${employee.id}/${now.toISOString().split('T')[0]}/${action}_${Date.now()}.jpg`;
          const { data: st } = await supabase.storage.from('attendance-photos').upload(path, blob);
          photoPath = st?.path;
        }
      }

      if (activeRecord) {
        await supabase.from('attendance').update({ check_out: now.toISOString(), photo_exit_url: photoPath }).eq('id', activeRecord.id);
      } else {
        await supabase.from('attendance').insert([{
          employee_id: employee.id, 
          id_configuration: idConfig,
          check_in: now.toISOString(), 
          photo_entry_url: photoPath, 
          type: 'work'
        }]);
      }

      playSuccessSound();
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      
      const timeStr = now.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
      showOverlay('success', `Odbito ${action}: o ${timeStr}`);
      
      await fetchEmployeeStatus(employee.id);

    } catch (e) {
      showOverlay('error', e.message || 'Błąd zapisu danych');
    }
  };

  const handleMonthChange = (direction) => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedMonth(newDate);
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: '#f5f2ed', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: { xs: 10, sm: 12 }, pb: 6, px: 2 }}>
      <ScanOverlay state={overlay} msg={overlayMsg} />

      {/* Hidden camera */}
      <Box sx={{ display: 'none' }}><WorkerCameraScanner ref={cameraRef} /></Box>

      {/* Górny przełącznik (Suwak / Zakładki) */}
      <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: '#eae5dc', p: 0.5, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            minHeight: 44,
            '& .MuiTabs-indicator': { borderRadius: 3, height: '100%', opacity: 0.15, bgcolor: '#1976d2' }
          }}
        >
          <Tab icon={<AccessTimeIcon fontSize="small" />} iconPosition="start" label="Rejestracja" sx={{ fontWeight: 700, borderRadius: 2.5, minHeight: 44, px: 3 }} />
          <Tab icon={<CalendarMonthIcon fontSize="small" />} iconPosition="start" label="Statystyki" sx={{ fontWeight: 700, borderRadius: 2.5, minHeight: 44, px: 3 }} />
        </Tabs>
      </Paper>

      {/* Komponent tablicy ogłoszeniowej */}

        <Box sx={{mt: 0, width: '98%'}}>
            <NoticeboardForWorker profileType="driver" idConfig={idConfig}/>
        </Box>

      {loading ? (
        <CircularProgress sx={{ mt: 4 }} />
      ) : !employee ? (
        <Alert severity="warning" sx={{ borderRadius: 3, maxWidth: 450, width: '100%' }}>
          Nie znaleziono profilu pracownika powiązanego z Twoim kontem.
        </Alert>
      ) : activeTab === 0 ? (
        /* ──────── ZAKŁADKA 1: REJESTRACJA (START / STOP) ──────── */
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Typography variant="h2" sx={{ fontWeight: 200, color: '#444', letterSpacing: 6, mb: 3 }}>
            {clock}
          </Typography>

          <Card sx={{ bgcolor:'#f5f2ed', maxWidth: 450, width: '100%', borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', textAlign: 'center', p: 2 }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#222', mb: 1 }}>
                {employee.name} {employee.surname}
              </Typography>

              <Typography variant="body2" sx={{ color: '#666', mt: 2 }}>
                Czas pracy dzisiaj:
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#222' }}>
                {formatMinutesToHours(hoursToday)}
              </Typography>

              {activeRecord && (
                <Typography variant="body2" sx={{ color: '#2e7d32', mb: 3, fontWeight: 600 }}>
                  Trwa zmiana od {new Date(activeRecord.check_in).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                color={activeRecord ? 'error' : 'success'}
                onClick={handleStartStop}
                startIcon={activeRecord ? <StopIcon sx={{ fontSize: '2rem !important' }} /> : <PlayArrowIcon sx={{ fontSize: '2rem !important' }} />}
                sx={{
                  py: 2.5,
                  borderRadius: 4,
                  fontWeight: 800,
                  fontSize: '1.5rem',
                  textTransform: 'none',
                  boxShadow: activeRecord ? '0 8px 24px rgba(211,47,47,0.3)' : '0 8px 24px rgba(46,125,50,0.3)',
                  transition: 'transform 0.1s ease',
                  '&:active': { transform: 'scale(0.98)' }
                }}
              >
                {activeRecord ? 'STOP (Zakończ)' : 'START (Rozpocznij)'}
              </Button>
            </CardContent>
          </Card>
        </Box>
      ) : (
        /* ──────── ZAKŁADKA 2: STATYSTYKI MIESIĘCZNE ──────── */
        <Box sx={{ maxWidth: 650, width: '100%' }}>
          {/* Nawigacja po miesiącach */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: 4, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff' }}>
            <IconButton onClick={() => handleMonthChange(-1)} color="primary">
              <ArrowBackIosNewIcon />
            </IconButton>

            <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
              {selectedMonth.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })}
            </Typography>

            <IconButton onClick={() => handleMonthChange(1)} color="primary">
              <ArrowForwardIosIcon />
            </IconButton>
          </Paper>

          {/* Podsumowanie godzin */}
          <Card sx={{ borderRadius: 4, mb: 3, bgcolor: '#A84355', color: '#fff', boxShadow: '0 8px 24px rgba(25,118,210,0.25)' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                Łącznie przepracowano w tym miesiącu
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 800, mt: 0.5 }}>
                {formatMinutesToHours(monthlyTotalMinutes)}
              </Typography>
            </CardContent>
          </Card>

          {/* Tabela pojedynczych wpisów */}
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, overflow: 'hidden' }}>
            {loadingStats ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : monthlyLogs.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ color: '#888' }}>
                  Brak wpisów obecności w wybranym miesiącu.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead sx={{ bgcolor: '#fafafa' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Wejście</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Wyjście</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Czas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyLogs.map((log) => {
                    const start = new Date(log.check_in);
                    const end = log.check_out ? new Date(log.check_out) : null;
                    const durationMin = ((end ? end.getTime() : Date.now()) - start.getTime()) / 1000 / 60;

                    return (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {start.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </TableCell>
                        <TableCell>
                          {start.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell>
                          {end ? (
                            end.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
                          ) : (
                            <Chip label="W trakcie" color="success" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatMinutesToHours(durationMin)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

export default DriverAttendanceGateKeeper;